import { Control, Controller, useWatch } from 'react-hook-form';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  makeStyles,
  Paper,
  Select,
  TextField,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';

import { ORGANIZATION_TYPE } from '../../types/organization.type';
import { DISCOUNT_TYPES } from '../../../shared/types/discount.type';
import { CreateOrganizationFormValues } from './create-organization-form.type';

type Props = {
  control: Control<CreateOrganizationFormValues>;
};

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',

    '& > h4': {
      margin: 0,
      opacity: 0.7,
    },
  },
  fieldsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem 0.75rem',
  },
  topRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'stretch',
  },
  identityColumn: {
    flexBasis: 'calc(70% - 0.5rem)',
  },
  offerColumn: {
    flexBasis: 'calc(30% - 0.5rem)',
  },
  identityHeaderRow: {
    display: 'flex',
    gap: '1rem',
  },
  identityType: {
    flexBasis: '30%',
  },
  identityName: {
    flexBasis: '70%',
  },
  addressTopRow: {
    display: 'flex',
    gap: '1rem',
  },
  addressStreet: {
    flexBasis: '50%',
  },
  addressCity: {
    flexBasis: '30%',
  },
  addressZipCode: {
    flexBasis: '20%',
  },
  discountRow: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
  },
  discountValue: {
    flex: 1,
  },
  toggleGroup: {
    height: 40,
  },
});

export function InformationsStep({ control }: Props) {
  const classes = useStyles();
  const reduction = useWatch({ control, name: 'reduction' });

  return (
    <>
      <section className={classes.topRow}>
        <section className={`${classes.section} ${classes.identityColumn}`}>
          <h4>Identit&eacute;</h4>
          <Paper className={classes.fieldsGroup}>
            <section className={classes.identityHeaderRow}>
              <div className={classes.identityType}>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select native label="Type" {...field}>
                        <option value={ORGANIZATION_TYPE.CONCIERGERY}>Conciergerie</option>
                        <option value={ORGANIZATION_TYPE.COMPANY}>Entreprise</option>
                      </Select>
                    </FormControl>
                  )}
                />
              </div>
              <div className={classes.identityName}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                  render={({ field }) => (
                    <TextField label="Nom" type="text" variant="outlined" fullWidth required {...field} />
                  )}
                />
              </div>
            </section>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Description"
                  type="text"
                  variant="outlined"
                  multiline
                  minRows={3}
                  maxRows={3}
                  fullWidth
                  inputProps={{ maxLength: 250 }}
                  {...field}
                />
              )}
            />
          </Paper>
        </section>
        <section className={`${classes.section} ${classes.offerColumn}`}>
          <h4>Offre</h4>
          <Paper className={classes.fieldsGroup}>
            <div className={classes.discountRow}>
              <Controller
                name="discount"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    className={classes.discountValue}
                    label="Remise"
                    type="number"
                    variant="outlined"
                    inputProps={{ min: 0 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {reduction === DISCOUNT_TYPES.PERCENT ? '%' : '€'}
                        </InputAdornment>
                      ),
                    }}
                    onChange={(event) => field.onChange(Number(event.target.value))}
                  />
                )}
              />
              <Controller
                name="reduction"
                control={control}
                render={({ field }) => (
                  <ToggleButtonGroup
                    className={classes.toggleGroup}
                    exclusive
                    size="small"
                    value={field.value}
                    onChange={(_event, value) => {
                      if (value) field.onChange(value);
                    }}
                    aria-label="Type de remise"
                  >
                    <ToggleButton value={DISCOUNT_TYPES.PERCENT}>%</ToggleButton>
                    <ToggleButton value={DISCOUNT_TYPES.FLAT}>€</ToggleButton>
                  </ToggleButtonGroup>
                )}
              />
            </div>
            <Controller
              name="isRestricted"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(event) => field.onChange(event.target.checked)}
                      color="primary"
                    />
                  }
                  label="Offre restreinte aux membres"
                />
              )}
            />
          </Paper>
        </section>
      </section>
      <section className={classes.section}>
        <h4>Adresse</h4>
        <Paper className={classes.fieldsGroup}>
          <section className={classes.addressTopRow}>
            <div className={classes.addressStreet}>
              <Controller
                name="address.street"
                control={control}
                rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                render={({ field }) => (
                  <TextField label="Rue" type="text" variant="outlined" fullWidth required {...field} />
                )}
              />
            </div>
            <div className={classes.addressCity}>
              <Controller
                name="address.city"
                control={control}
                rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                render={({ field }) => (
                  <TextField label="Ville" type="text" variant="outlined" fullWidth required {...field} />
                )}
              />
            </div>
            <div className={classes.addressZipCode}>
              <Controller
                name="address.zipCode"
                control={control}
                rules={{ required: true, validate: (value) => value.trim().length > 0 }}
                render={({ field }) => (
                  <TextField label="Code postal" type="text" variant="outlined" fullWidth required {...field} />
                )}
              />
            </div>
          </section>
          <Controller
            name="address.informations"
            control={control}
            render={({ field }) => (
              <TextField
                label="Informations de livraison"
                type="text"
                variant="outlined"
                multiline
                minRows={2}
                maxRows={2}
                fullWidth
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </Paper>
      </section>
    </>
  );
}
